import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link, Check, Pencil, FolderInput, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

type ClassData = {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  video_url: string;
  categories: string[];
  created_at: string;
};

type Category = {
  id: string;
  name: string;
  img_url?: string;
};

interface ClassListProps {
  classes: ClassData[];
  categories: Category[];
  isAdmin: boolean;
  copiedClassId: string | null;
  onCopyLink: (classId: string) => void;
  onEdit: (class_: ClassData) => void;
  onAddClass: () => void;
  onMoveToFolder?: (class_: ClassData) => void;
  currentFolderId?: string | null;
}

// Custom hook for responsive design
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 768,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

const ClassList = ({
  classes,
  categories,
  isAdmin,
  copiedClassId,
  onCopyLink,
  onEdit,
  onAddClass,
  onMoveToFolder,
  currentFolderId
}: ClassListProps) => {
  const navigate = useNavigate();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width < 768;

  // Check user's motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const formatTime = (timestamp: string) => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  const getClassCategories = (class_: ClassData) => {
    return categories
      .filter(category => class_.categories.includes(category.id))
      .map(category => category.name)
      .join(', ');
  };

  const truncateDescription = useCallback((description: string) => {
    const maxLength = isMobile ? 20 : 100; // Very short for mobile, longer for desktop
    const cleaned = description.replace(/[#*`]/g, ''); // Remove markdown characters
    
    if (cleaned.length <= maxLength) return cleaned;
    return `${cleaned.substring(0, maxLength).trim()}...`;
  }, [isMobile]);

  return (
    <div className="w-full">
      {/* Add Class Button */}
      {isAdmin && (
        <motion.button
          onClick={onAddClass}
          className="w-full mb-4 bg-[#141C28]/60 backdrop-blur-lg backdrop-saturate-150 border border-white/5 rounded-lg px-4 py-3 flex items-center justify-center gap-2 hover:bg-[#1a2533] transition-colors"
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <span className="text-[#c9fffc]">Add New Class</span>
        </motion.button>
      )}

      {/* Classes List */}
      <AnimatePresence>
        <div className="space-y-3">
          {classes.map((class_, index) => (
            <motion.div
              key={class_.id}
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.3, 
                delay: prefersReducedMotion ? 0 : index * 0.05 
              }}
              className="w-full bg-[#141C28]/60 backdrop-blur-lg backdrop-saturate-150 border border-white/5 rounded-lg overflow-hidden hover:border-[#c9fffc]/30 transition-colors"
            >
              <motion.div
                className="w-full p-4 relative cursor-pointer"
                onClick={() => navigate(`/classroom/${class_.id}${currentFolderId ? `?folder=${currentFolderId}` : ''}`)}
                whileHover={{ backgroundColor: 'rgba(26, 37, 51, 0.4)' }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 pr-4">
                    {/* Title */}
                    <h3 className="text-lg font-medium text-white mb-1 tracking-tight">
                      {class_.title}
                    </h3>
                    
                    {/* Description - shorter on mobile */}
                    <div className="text-sm text-gray-300 mb-2">
                      {truncateDescription(class_.description)}
                    </div>
                    
                    {/* Metadata - hide categories on mobile */}
                    <div className="flex items-center text-xs text-gray-400 space-x-3">
                      <span>{formatTime(class_.created_at)}</span>
                      {!isMobile && getClassCategories(class_) && (
                        <>
                          <span>•</span>
                          <span>{getClassCategories(class_)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions - simplified on mobile */}
                  <div className="flex items-center gap-2">
                    {/* For mobile, only show share button */}
                    {isMobile ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onCopyLink(class_.id);
                        }}
                        className="p-2 bg-[#1a2533]/80 rounded-full hover:bg-[#232f3e] transition-colors"
                      >
                        {copiedClassId === class_.id ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Link className="w-4 h-4 text-[#c9fffc]" />
                        )}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/classroom/${class_.id}${currentFolderId ? `?folder=${currentFolderId}` : ''}`);
                          }}
                          className="p-2 bg-[#1a2533]/80 rounded-full hover:bg-[#232f3e] transition-colors"
                        >
                          <ExternalLink className="w-4 h-4 text-[#c9fffc]" />
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onCopyLink(class_.id);
                          }}
                          className="p-2 bg-[#1a2533]/80 rounded-full hover:bg-[#232f3e] transition-colors"
                        >
                          {copiedClassId === class_.id ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Link className="w-4 h-4 text-[#c9fffc]" />
                          )}
                        </button>

                        {onMoveToFolder && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onMoveToFolder(class_);
                            }}
                            className="p-2 bg-[#1a2533]/80 rounded-full hover:bg-[#232f3e] transition-colors"
                          >
                            <FolderInput className="w-4 h-4 text-[#c9fffc]" />
                          </button>
                        )}

                        {isAdmin && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(class_);
                            }}
                            className="p-2 bg-[#1a2533]/80 rounded-full hover:bg-[#232f3e] transition-colors"
                          >
                            <Pencil className="w-4 h-4 text-[#c9fffc]" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
          
          {classes.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="w-full py-10 text-center text-gray-400"
            >
              <p>No classes found in this folder.</p>
              {isAdmin && (
                <button
                  onClick={onAddClass}
                  className="mt-2 text-[#c9fffc] hover:underline"
                >
                  Add your first class
                </button>
              )}
            </motion.div>
          )}
        </div>
      </AnimatePresence>
    </div>
  );
};

export default ClassList;
